=====================================================================
  HOMEPAGE - Pagina Principale
  =====================================================================
  
  Pagina principale con layout split a due sezioni e striscia orizzontale.
  
  FEATURES:
  - Caricamento contenuto da mockData in store
  - Rendering dinamico sezioni da state.pageSections
  - Stati loading/error
  - Layout fullscreen (100vh)
  
  =====================================================================
-->

<template>
  <div>
    <main class="homepage">
      
      <div v-if="state.error" class="error">
        <p>Si è verificato un errore durante il caricamento.</p>
        <button @click="retry">Riprova</button>
      </div>

      <div v-else class="content">
        <template v-for="(section, index) in state.pageSections" :key="`${section.type}-${index}`">
          <component
            v-if="getComponent(section.type)"
            :is="getComponent(section.type)"
            :content="section.content"
          />
        </template>

        <!-- LINK PRIVACY / CONTATTI in alto, vicino al bordo della sezione attiva (solo desktop) -->
        <Transition name="banner-fade" mode="out-in">
          <div
            v-if="footerLinks.items.length"
            :key="state.hoverSection"
            class="top-corner-links"
            :class="`top-corner-links--${footerLinks.side}`"
            @mouseenter="setHoverActive(true)"
            @mouseleave="setHoverActive(false)"
          >
            <a
              v-for="l in footerLinks.items"
              :key="l.label"
              :href="l.href"
              class="top-corner-link"
              target="_blank"
              rel="noopener noreferrer"
            >{{ l.label }}</a>
          </div>
        </Transition>

        <!-- BANNER ESTETICO AL FONDO DELLA PAGINA -->
        <div class="page-footer-banner" v-if="splitLinksContent">
          <Transition name="banner-fade" mode="out-in">
            <img 
              :key="state.hoverSection"
              :src="currentBannerImage"
              alt=""
              class="footer-banner-image"
              :class="{ 
                'align-left': state.hoverSection === 'left',
                'align-right': state.hoverSection === 'right'
              }"
            />
          </Transition>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { state, loadPageContent, setHoverActive } from '@/store/index.js'

import SectionSplitLinks from '@/components/sections/home-page/SectionSplitLinks.vue'
import SectionHorizontalStrip from '@/components/sections/home-page/SectionHorizontalStrip.vue'

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Recupera il contenuto della sezione split-links per accedere alle immagini del banner
 */
const splitLinksContent = computed(() => {
  const section = state.pageSections.find(s => s.type === 'section-split-links')
  return section ? section.content : null
})

/**
 * Immagine del banner corrente in base all'hover globale
 */
const currentBannerImage = computed(() => {
  const content = splitLinksContent.value
  if (!content) return ''
  
  const images = state.hoverSection === 'right' 
    ? content.rightCarouselImage 
    : content.leftCarouselImage
    
  return Array.isArray(images) ? images[0] : images
})

/**
 * Link footer (Privacy/Contatti) mostrati in alto, nell'angolo della sezione attiva.
 * Seguono la sezione attiva: Cassa (hover left / default) → in alto a sinistra (suo bordo);
 * Fondazione (hover right) → in alto a destra (suo bordo).
 * L'URL si costruisce sulla base dell'url del sito (leftLink/rightLink) + il
 * path definito nel json, così il dominio resta scritto in un solo posto.
 */
const footerLinks = computed(() => {
  const content = splitLinksContent.value
  // Di default (nessun hover su nessun lato) i link non si mostrano da nessuna parte.
  if (!content || !state.hoverActive) return { side: 'left', items: [] }

  const isFondazione = state.hoverSection === 'right'
  const link = isFondazione ? content.rightLink : content.leftLink
  const base = (link?.url || '').replace(/\/$/, '')

  const items = (link?.footerLinks || []).map(it => ({
    label: it.label,
    href: it.url || base + it.path
  }))

  return { side: isFondazione ? 'right' : 'left', items }
})

onMounted(async () => {
  await loadPageContent('homepage/')
  
  // LOGICA PAGINA FANTASMA (REDIRECT AUTOMATICO)
  // Se l'URL finisce con 'app-redirect', esegue il reindirizzamento allo store
  if (window.location.pathname.endsWith('app-redirect') || window.location.pathname.endsWith('app-redirect/')) {
    const section = state.pageSections.find(s => s.type === 'section-split-links')
    const platforms = section?.content?.leftLink?.buttons?.[0]?.platforms
    
    if (platforms) {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      let target = platforms.desktop

      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        target = platforms.ios
      } else if (/android/i.test(userAgent)) {
        target = platforms.android
      }

      if (target) window.location.replace(target)
    }
  }
})

function retry() {
  loadPageContent('homepage/')
}

/**
 * Risolve il componente della sezione in base al tipo restituito dal backend
 * 
 * @param {string} type - Tipo di sezione (es. 'section-split-links')
 * @returns {Component|null} Il componente Vue corrispondente o null
 */
function getComponent(type) {
  switch (type) {
    case 'section-split-links':
      return SectionSplitLinks
    case 'section-horizontal-strip':
      return SectionHorizontalStrip
    default:
      return null
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";

.homepage {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: 100svh; // fix iOS (15.4+): esclude la barra di navigazione
                    // browser senza svh ignorano questa riga e restano su 100vh
  }
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  @media (max-width: 768px) {
    height: 100%;
    overflow: hidden; // Preveniamo lo scroll nativo per usare le gesture
  }
}

/* STILI BANNER DI FONDO PAGINA */
.page-footer-banner {
  position: relative; /* Ancora per i link Privacy/Contatti sovrapposti */
  margin-top: auto; /* Lo spinge al fondo se il contenuto è meno di 100vh */
  width: 100%;
  line-height: 0;
  background: $clr-bg-page;
  z-index: 50;
  overflow: hidden; // Necessario per l'effetto zoom con ritaglio

  // Mobile: nascosto — il nuovo layout mobile gestisce l'immagine internamente
  @media (max-width: 1024px) {
    display: none;
  }
}

/* LINK PRIVACY / CONTATTI IN ALTO, vicino al bordo della sezione attiva (solo desktop) */
.top-corner-links {
  position: absolute;
  top: $space-1;
  display: flex;
  z-index: 60;
  line-height: 1;

  &--left  { left: $space-1-5; right: auto; }
  &--right { right: $space-1-5; left: auto; }

  // Su mobile/tablet i link restano nell'immagine bottom (gestiti da SectionSplitLinks)
  @media (max-width: 1024px) { display: none; }
}

.top-corner-link {
  font-family: 'Karla', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase; // maiuscolo (presentazione: la label nel json resta invariata)
  color: rgba($clr-brand-dark, 0.30); // grigio chiarissimo
  text-decoration: none;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;

  &:hover {
    color: rgba($clr-brand-dark, 0.55);
  }

  // separatore | tra un link e l'altro, sulla stessa riga
  & + &::before {
    content: '|';
    // Stesso separatore, ma con testo alternativo vuoto: senza, il lettore di schermo
    // leggeva il link come "|PRIVACY". I browser che non conoscono la sintassi "/"
    // scartano questa riga e tengono quella sopra. (2026-09-16)
    content: '|' / '';
    margin: 0 $space-0-5;
    color: rgba($clr-brand-dark, 0.20);
    pointer-events: none;
  }
}

.footer-banner-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 25vh;
  object-fit: cover;
  object-position: center bottom; // Default
  vertical-align: bottom;
  transition: object-position 0.7s ease; // Transizione fluida dello spostamento laterale

  &.align-left {
    object-position: left bottom;
  }

  &.align-right {
    object-position: right bottom;
  }

  @media (max-width: 1000px) {
    width: 200%;
    max-width: none;
    position: relative;
    transition: opacity 0.7s ease, left 0.7s ease, right 0.7s ease, transform 0.7s ease;
    transform-origin: bottom; 

    &.align-left {
      left: 0;
      transform: scale(0.95);
      transform-origin: left bottom;
      object-position: left bottom;
    }

    &.align-right {
      left: 100%;
      transform: translateX(-100%) scale(0.95);
      transform-origin: right bottom;
      object-position: right bottom;
    }
  }

  @media (max-width: 768px) {
    max-height: 12vh;
    width: 260%;
  }

  @media (max-width: 480px) {
    width: 320%;
  }

  @media (max-width: 360px) {
    width: 340%;
  }
}

/* TRANSIZIONI */
.banner-fade-enter-active, .banner-fade-leave-active {
  transition: opacity 0.7s ease;
}
.banner-fade-enter-from, .banner-fade-leave-to {
  opacity: 0;
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: $space-2;
}


.error {
  text-align: center;
  color: #c0392b;
  
  button {
    margin-top: $space-1;
    padding: $space-0-5 $space-1-5;
    background: $clr-primary;
    color: $text-color;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: $font-base;
    
    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
